import { useContextSelector } from "use-context-selector";

import { IStore, StoreContext } from "..";
import {
  ChartSettings,
  ChartType,
  IAxis,
  ICubic,
  IReports,
  QuickViewMeta,
  ReportFilters,
  ReportsMeta,
  ReportsQueries,
  ReportTableColumn,
  ReportTableData
} from "../types";

export const useReportsQueriesSelector = (): ReportsQueries =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.queries);

export const useReportsMetaSelector = (): ReportsMeta =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.meta);

export const useReportsDealFilterSelector = (): ReportFilters =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.meta.dealFilters);

export const getShownCalculatedFields = (): ReportsMeta["calculatedFields"] =>
  useContextSelector(StoreContext, (state: IStore) => {
    return state.reports.queries.calculatedFields;
  });

export const useGraphDataFormSelector = (): Record<
  string,
  { measure: string; groupBy: string; chartType?: ChartType; xAxis?: IAxis; yAxis?: IAxis }
> => useContextSelector(StoreContext, (state: IStore) => state.reports.queries.graphDataFormation);

export const useReportsTableSelector = (): ReportTableData | null =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.tableData);

export const useReportsSecTableSelector = (): ReportTableData | null =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.secondaryTableData);

export const useIsRowExpandSelector = (rowId: string): boolean =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.expandedRows.has(rowId));

export const useReportsFiltersSelector = (): ReportFilters =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.queries.filters);

export const useCalculatedSelector = (): ReportsMeta["calculatedFields"] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.meta.calculatedFields);

export const useSnapshotCubicConfigSelector = (): ICubic =>
  useContextSelector(StoreContext, (state: IStore) => {
    return state.reports.queries.cubicSnapshot;
  });

export const useCashflowCubicConfigSelector = (): ICubic =>
  useContextSelector(StoreContext, (state: IStore) => {
    return state.reports.queries.cubicCashflow;
  });

// ---- Client snapshot (Client Report tab) config selectors ----
export const useClientSnapshotCubicConfigSelector = (): ICubic =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.queries.cubicClientSnapshot);

export const useClientGraphDataFormSelector = (): Record<string, ChartSettings> =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.queries.clientGraphDataFormation);

export const useClientReportTableColumnsSelector = (): ReportTableColumn[] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.queries.clientReportTableColumns);

export const useQuickViewMetaSelector = (): QuickViewMeta =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.quick_view_meta);

export const useRefetchReportsSelect = (): IReports["refetchReports"] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.refetchReports);

export const useIgnoreDefaultView = (): IReports["ignoreDefaultView"] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.ignoreDefaultView);

export const useQuickViewQueriesSelect = (): IReports["quickViewQueries"] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.quickViewQueries);

export const useIsHedgeFundSelect = (): IReports["is_hedge_fund"] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.is_hedge_fund);

export const useGraphBenchmarkValuesSelect = (): ReportsQueries["graphBenchmark"] =>
  useContextSelector(StoreContext, (state: IStore) => state.reports.queries.graphBenchmark);

export const useCorrelationMatrixSettingsSelector = () =>
  useContextSelector(StoreContext, (store: IStore) => store.reports.correlationMatrixSettings);
